import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Row, Col, message } from "antd";
import axios from "axios";
import {
  POST_UPDATE_CUSTOMER,
  GET_GEO_DB2,
  HEADERS_EXPORT_BEN,
} from "../../../../API/apiUrls";

const EditCustomerContactModal = ({
  open,
  onClose,
  mode, // "phone" | "address"
  customer,
  dataType,
  addresses = [],
  editingAddress,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [geoList, setGeoList] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [selectedProvcod, setSelectedProvcod] = useState(null);

  useEffect(() => {
    if (!open || mode !== "address") return;
    let active = true;
    setGeoLoading(true);
    axios
      .get(GET_GEO_DB2, { headers: HEADERS_EXPORT_BEN })
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setGeoList(list);
      })
      .catch((error) => {
        console.error("GET_GEO_DB2 error:", error);
        if (active) message.error("โหลดข้อมูลจังหวัด/อำเภอไม่สำเร็จ");
      })
      .finally(() => active && setGeoLoading(false));
    return () => {
      active = false;
    };
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    if (mode === "phone") {
      form.resetFields();
      form.setFieldsValue({
        MOBILENO: customer?.MOBILENO || "",
        MAIN_ADDRNO: undefined,
      });
    } else if (mode === "address") {
      if (editingAddress) {
        const provcod = editingAddress.PROVCOD
          ? String(editingAddress.PROVCOD)
          : undefined;
        setSelectedProvcod(provcod || null);
        form.setFieldsValue({
          ADDR1: editingAddress.ADDR1,
          MOOBAN: editingAddress.MOOBAN,
          SOI: editingAddress.SOI,
          TUMB: editingAddress.TUMB,
          PROVCOD: provcod,
          AUMPCOD: editingAddress.AUMPCOD
            ? String(editingAddress.AUMPCOD)
            : undefined,
          ZIP: editingAddress.ZIP,
          TELP: editingAddress.TELP,
        });
      } else {
        form.resetFields();
        setSelectedProvcod(null);
      }
    }
  }, [open, mode, editingAddress, customer, form]);

  const provinceOptions = useMemo(() => {
    const seen = new Map();
    geoList.forEach((g) => {
      const code = g.PROVCOD != null ? String(g.PROVCOD) : null;
      if (code && !seen.has(code)) seen.set(code, g.PROVDES);
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [geoList]);

  const districtOptions = useMemo(() => {
    if (!selectedProvcod) return [];
    return geoList
      .filter((g) => String(g.PROVCOD) === String(selectedProvcod))
      .map((g) => ({
        value: g.AUMPCOD != null ? String(g.AUMPCOD) : "",
        label: g.AUMPDES,
      }));
  }, [geoList, selectedProvcod]);

  const mainAddrOptions = useMemo(
    () =>
      addresses.map((a) => ({
        value: String(a.ADDRNO),
        label: `ที่อยู่ ${a.ADDRNO} - ${a.ADDR1 || ""}`,
      })),
    [addresses],
  );

  const handleProvinceChange = (value) => {
    setSelectedProvcod(value);
    form.setFieldsValue({ AUMPCOD: undefined });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const body = { DATA_TYPE: dataType, CUSCOD: customer?.CUSCOD };

      if (mode === "phone") {
        body.MOBILENO = values.MOBILENO;
        body.USERID = localStorage.getItem("USER_ID");
        if (values.MAIN_ADDRNO) body.MAIN_ADDRNO = values.MAIN_ADDRNO;
      } else {
        if (editingAddress?.ADDRNO) body.ADDRNO = editingAddress.ADDRNO;
        body.ADDR1 = values.ADDR1;
        body.MOOBAN = values.MOOBAN || "-";
        body.SOI = values.SOI || "-";
        body.TUMB = values.TUMB;
        body.AUMPCOD = values.AUMPCOD;
        body.PROVCOD = values.PROVCOD;
        body.ZIP = values.ZIP;
        body.TELP = values.TELP;
      }
      console.log("body", body);

      await axios.post(POST_UPDATE_CUSTOMER, body, {
        headers: HEADERS_EXPORT_BEN,
      });

      message.success("บันทึกข้อมูลสำเร็จ");
      onSuccess?.();
      onClose();
    } catch (error) {
      if (error?.errorFields) return;
      console.error("EditCustomerContactModal submit error:", error);
      message.error(error?.response?.data?.message || "บันทึกข้อมูลไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    mode === "phone"
      ? "แก้ไขเบอร์โทรศัพท์หลัก"
      : editingAddress
        ? `แก้ไขที่อยู่ ${editingAddress.ADDRNO}`
        : "เพิ่มที่อยู่ใหม่";

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={mode === "phone" ? 480 : 720}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        {mode === "phone" ? (
          <>
            <Form.Item
              name="MOBILENO"
              label="เบอร์โทรศัพท์หลัก"
              // rules={[{ required: true, message: "กรุณากรอกเบอร์โทรศัพท์" }]}
            >
              <Input placeholder="089-1234567" size="large" maxLength={12} />
            </Form.Item>
            <Form.Item name="MAIN_ADDRNO" label="ตั้งเป็นที่อยู่หลัก">
              <Select
                allowClear
                placeholder="ไม่เปลี่ยนที่อยู่หลัก"
                options={mainAddrOptions}
                size="large"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="ADDR1"
              label="ที่อยู่ (บ้านเลขที่/เลขที่หมู่)"
              rules={[{ required: true, message: "กรุณากรอกที่อยู่" }]}
            >
              <Input placeholder="99/1 หมู่บ้านตัวอย่าง" size="large" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="MOOBAN" label="หมู่บ้าน">
                  <Input placeholder="-" size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="SOI" label="ซอย">
                  <Input placeholder="-" size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="PROVCOD"
                  label="จังหวัด"
                  rules={[{ required: true, message: "กรุณาเลือกจังหวัด" }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="เลือกจังหวัด"
                    optionFilterProp="label"
                    options={provinceOptions}
                    onChange={handleProvinceChange}
                    loading={geoLoading}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="AUMPCOD"
                  label="อำเภอ/เขต"
                  rules={[{ required: true, message: "กรุณาเลือกอำเภอ" }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder={
                      selectedProvcod
                        ? "เลือกอำเภอ/เขต"
                        : "กรุณาเลือกจังหวัดก่อน"
                    }
                    optionFilterProp="label"
                    options={districtOptions}
                    disabled={!selectedProvcod}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="TUMB"
                  label="ตำบล/แขวง"
                  // rules={[{ required: true, message: "กรุณากรอกตำบล" }]}
                >
                  <Input placeholder="ตำบล/แขวง" size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ZIP"
                  label="รหัสไปรษณีย์"
                  rules={[
                    { required: true, message: "กรุณากรอกรหัสไปรษณีย์" },
                    {
                      pattern: /^[0-9]{5}$/,
                      message: "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก",
                    },
                  ]}
                >
                  <Input placeholder="10900" maxLength={5} size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="TELP"
                  label="เบอร์โทรศัพท์ที่อยู่นี้(ได้ 2 เบอร์)"
                >
                  <Input
                    placeholder="089-9999999 / 061-2142313"
                    size="large"
                    maxLength={30}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditCustomerContactModal;
